<script lang="ts" setup>
import { decrypt } from '~/utils/encrypt-string'

const { t } = useLang()
const marketplaceLock = useMarketplaceLock()
const passwordInput = ref('')
const passwordError = ref('')
const ready = ref(false)

const handleSubmit = (value: { 'mp-lock-password': string }) => {
  passwordError.value = ''
  const decrypted = decrypt(marketplaceLock.pass || '')

  const password = String(value['mp-lock-password']).trim()

  if (password === '') {
    return
  }

  try {
    if (password !== decrypted) {
      passwordError.value = t('mp_lock.error_incorrect_lock_password')
    } else {
      // Use the centralized localStorage from useMarketplaceLock
      const localPass = useLocalStorage<string>('mpLockPass', '')
      localPass.value = password
    }
  } catch (error) {
    passwordError.value = t('mp_lock.error_incorrect_lock_password')
  }
}

onMounted(() => {
  nextTick(() => {
    ready.value = true
  })
})
</script>

<template>
  <div class="mp-lock-component">
    <ClientOnly>
      <template #fallback>
        <div class="flex flex-1 justify-center items-center w-full my-8">
          <Loader />
        </div>
      </template>
      <template v-if="!marketplaceLock.isUnlocked.value && ready">
        <div class="flex flex-1 self-center">
          <Container>
            <div class="lock-screen">
              <div class="rounded-sm flex justify-center flex-col lg:min-w-[750px] lg:p-8 items-center">
                <div v-if="marketplaceLock.timeLeft.value && marketplaceLock.timerLock.value" class="pb-5">
                  <CartCounter :end-time="marketplaceLock.endTime || ''" refresh />
                </div>
                <h4 v-if="marketplaceLock.title" class="text-center mb-2" v-html="marketplaceLock.title" />
                <span v-if="marketplaceLock.description" class="text-center" v-html="marketplaceLock.description" />
                <FormKit
                  v-show="marketplaceLock.passwordLock"
                  type="form"
                  :incomplete-message="false"
                  :actions="false"
                  class="w-full"
                  @submit="handleSubmit">
                  <div class="password-section flex pt-11">
                    <InputText
                      class="w-full"
                      :value="passwordInput"
                      :placeholder="t('mp_lock.lock_input_placeholder')"
                      name="mp-lock-password"
                      @handle="(value) => (passwordInput = value)" />
                    <Button submit>{{ t('common.enter') }}</Button>
                  </div>
                  <span v-if="passwordError" class="text-sm text-red">{{ passwordError }}</span>
                </FormKit>
              </div>
            </div>
          </Container>
        </div>
      </template>
    </ClientOnly>
  </div>
</template>

<style lang="postcss" scoped>
.mp-lock-component {
  @apply flex flex-1;

  .lock-screen {
    @apply flex items-center justify-center py-20;

    :deep(.cart-counter-component) {
      @apply h-auto flex-wrap justify-center;

      .clock-icon {
        @apply h-[69px];
      }

      .counter {
        @apply text-[52px];
      }
    }
  }

  .password-section {
    :deep(.formkit-outer) {
      margin: 0;
      width: 100%;
      flex: 3;

      .formkit-inner {
        border-radius: theme('vars.borderRadiusHeavy');
        width: 100%;
        height: 50px;
        border: 1px solid theme('colors.brand.primary');
        box-shadow: none;

        input {
          @apply lg:min-w-[445px];
          height: 100%;
          font-weight: bold;
          border-radius: theme('vars.borderRadiusHeavy');

          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }

        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;

        &:focus-within {
          box-shadow: none;
        }
      }
    }

    button {
      @apply flex-1 w-full h-12 p-4 font-bold min-w-32 rounded-l-none;
    }
  }
}
</style>
